/**
 * =============================================================================
 * IDS CRM Web - Settings → Spare parts (spare parts, Push 2)
 * =============================================================================
 *
 * The list engineers search in Log Work (build spec 9.2), managed by
 * operations coordinators, managers and admins (SPARES_EDIT):
 *  1. how the list got here, and its last import;
 *  2. the customer charge: purchase cost plus the markup %, which only a
 *     manager or admin sets, with warnings while no markup is set and while
 *     items have no purchase cost;
 *  3. import the ServiceM8 materials report (.xlsx or .csv) and export the
 *     list as .xlsx;
 *  4. an import is always previewed first: nothing changes until Apply, which
 *     sends the same file with the preview's fingerprint and plan, so a
 *     different file or a list changed since the preview is refused;
 *  5-7. the list itself: filters, search, paging, and editing a row in place
 *     (name, purchase cost, and for a manager or admin the item's own
 *     charge), hide from engineers and show again;
 *  8. Parts used that need a price.
 *
 * Team leaders open this tab too, and see ONLY block 8: they are the ones who
 * price those parts (Correct on the job or ticket page). For them the page
 * never asks for the list or the pricing, which would answer 403.
 *
 * Every edit sends only the fields that changed. A coordinator never sends the
 * own-charge key (the API answers 403 to anyone but a manager or admin).
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { ukDayLabelOf, firstNameOf } from '../components/SavedLogWorkPanel';
import { canManageSpareParts, canSetSparePartsCharge } from '../utils/permissions';
import { formatMoney } from '../utils/money';
import { formatTime } from '../utils/format';
import { ukTodayKey } from '../utils/dates';
import { downloadFile } from '../utils/downloadFile';
import {
  costGbpText,
  costTenThousandths,
  formatPurchaseCost,
  poundsToPence,
  quantityText
} from '../utils/spareLines';
import './AdminSettings.css';
import './SparePartsSettings.css';

const PAGE_SIZE = 50;

const FILTERS = [
  ['active', 'Active'],
  ['hidden', 'Hidden from engineers'],
  ['retired', 'Retired'],
  ['no_cost', 'No cost'],
  ['all', 'All']
];

// The preview's count chips, in the order the office reads them.
const COUNT_CHIPS = [
  ['new', 'New'],
  ['name_changes', 'Name changes'],
  ['cost_changes', 'Cost changes'],
  ['brought_back', 'Brought back'],
  ['retired', 'Retired'],
  ['unchanged', 'Unchanged'],
  ['skipped', 'Skipped'],
  ['problem_rows', 'Left as they were'],
  ['pre_hidden', 'Hidden from engineers'],
  ['lookalike_groups', 'Lookalike Item Numbers']
];

const MARKUP_MESSAGE = 'Enter a markup between 0 and 500%, with at most 2 decimal places.';
const NAME_MESSAGE = 'Enter a name of up to 200 characters.';
const COST_MESSAGE = 'Enter a purchase cost of £0 or more, with at most 4 decimal places, or leave it blank.';
const CHARGE_MESSAGE = 'Enter the charge in pounds, such as 12.50, or leave it blank to use the markup.';

/** '20.00' is '20', '12.50' is '12.5'. */
const percentText = (value) => quantityText(String(value ?? ''));

/** A purchase cost for an edit box: '1150.5700' is '1150.57', '0.6375' stays. */
const costInputText = (cost) => {
  const shown = formatPurchaseCost(cost);
  return shown === '—' ? '' : shown.replace(/^£/, '').replace(/,/g, '');
};

const chargeInputText = (pence) => (pence === null || pence === undefined ? '' : (Number(pence) / 100).toFixed(2));

const plural = (n, one, many) => (n === 1 ? one : many);

async function answerOf(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

/** A preview list as an array, whether the API sends an array or { items, truncated }. */
function listRows(lists, key) {
  const value = lists ? lists[key] : null;
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.items)) return value.items;
  if (value && Array.isArray(value.rows)) return value.rows;
  return [];
}

/** Was this preview list cut at 2000 entries? The API says so in preview.truncated[key]. */
function listTruncated(previewData, key) {
  const lists = previewData ? previewData.lists : null;
  const value = lists ? lists[key] : null;
  return Boolean((value && value.truncated)
    || (previewData && previewData.truncated && typeof previewData.truncated === 'object' && previewData.truncated[key]));
}

/** How the row is charged per unit, as the list shows it. */
function chargeOf(row) {
  const basis = row.charge_basis || row.basis
    || (row.fixed_charge_minor_units !== null && row.fixed_charge_minor_units !== undefined ? 'fixed'
      : (costTenThousandths(row.purchase_cost_gbp) ? 'markup' : 'none'));
  if (basis === 'fixed') return { text: formatMoney(row.fixed_charge_minor_units ?? row.charge_per_unit_minor_units), own: true };
  if (basis === 'none') return { text: '£0.00 · needs a price', none: true };
  return { text: formatMoney(row.charge_per_unit_minor_units) };
}

function statusOf(row) {
  if (row.is_active === false) {
    const day = row.retired_at ? ` ${ukDayLabelOf(row.retired_at)}` : '';
    return `Retired${day}${row.source === 'import' ? ' by an import' : ''}`;
  }
  return row.engineer_hidden ? 'Hidden' : 'Active';
}

// ---------------------------------------------------------------------------
// The preview's lists: row number, Item Number, name, old → new
// ---------------------------------------------------------------------------

const PREVIEW_LISTS = [
  ['new', 'New', (r) => [r.row, r.part_number, r.name, `${formatPurchaseCost(r.cost)}${r.hidden ? ' · hidden from engineers' : ''}`]],
  ['name_changes', 'Name changes', (r) => [r.row, r.part_number, r.name, `${r.old_name} → ${r.name}`]],
  ['cost_changes', 'Cost changes', (r) => [r.row, r.part_number, r.name, `${formatPurchaseCost(r.old_cost)} → ${formatPurchaseCost(r.cost)}`]],
  ['brought_back', 'Brought back', (r) => [r.row, r.part_number, r.name, 'Back in the list']],
  ['retired', 'Retired (not in this file)', (r) => [null, r.part_number, r.name, r.source === 'import' ? 'Was imported' : 'Was not in the file']],
  ['skipped', 'Skipped', (r) => [r.row, r.part_number, r.name, r.text || r.reason]],
  ['problem_rows', 'In the file but left as they are, because the row has a problem', (r) => [r.row, r.part_number, r.name, r.text || r.reason]],
  ['pre_hidden', 'Hidden from engineers', (r) => [r.row, r.part_number, r.name, 'Hidden: not a part engineers fit']]
];

function PreviewList({ title, rows, truncated, toCells }) {
  if (rows.length === 0) return null;
  return (
    <details className="spare-preview-list">
      <summary>{title} ({rows.length}{truncated ? '+' : ''})</summary>
      {truncated && <p className="spare-muted">Only the first {rows.length.toLocaleString('en-GB')} are listed.</p>}
      <div className="users-table-wrap">
        <table className="users-table">
          <thead><tr><th>Row</th><th>Item Number</th><th>Name</th><th>Change</th></tr></thead>
          <tbody>
            {rows.map((r, index) => {
              const [rowNumber, number, name, detail] = toCells(r);
              return (
                <tr key={`${number || ''}-${rowNumber || ''}-${index}`}>
                  <td>{rowNumber ?? '—'}</td>
                  <td className="spare-mono">{number || '—'}</td>
                  <td>{name || '—'}</td>
                  <td>{detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function LookalikeList({ groups }) {
  if (!groups.length) return null;
  return (
    <details className="spare-preview-list">
      <summary>Lookalike Item Numbers ({groups.length})</summary>
      <p className="spare-muted">These Item Numbers look like the same part written two ways. Both are kept; check them in the file.</p>
      <div className="users-table-wrap">
        <table className="users-table">
          <thead><tr><th>Row</th><th>Item Number</th><th>Name</th><th>Purchase cost</th><th>Where</th></tr></thead>
          <tbody>
            {groups.map((group, groupIndex) => (Array.isArray(group) ? group : []).map((r, index) => (
              <tr key={`${groupIndex}-${index}`} className={index === 0 ? 'spare-group-start' : ''}>
                <td>{r.row ?? '—'}</td>
                <td className="spare-mono">{r.part_number}</td>
                <td>{r.name}</td>
                <td>{formatPurchaseCost(r.cost)}</td>
                <td>{r.in_file ? 'In this file' : 'In the list'}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Block 8: Parts used that need a price
// ---------------------------------------------------------------------------

function NeedsPrice({ rows, loading, error }) {
  return (
    <section className="admin-section spare-block" aria-labelledby="spare-needs-price-title">
      <div className="section-header">
        <h2 id="spare-needs-price-title">Parts used that need a price ({rows.length})</h2>
      </div>
      {loading && <p className="spare-muted">Loading…</p>}
      {error && <p className="spare-error" role="alert">{error}</p>}
      {!loading && !error && rows.length === 0 && <p className="spare-muted">No parts used need a price.</p>}
      {rows.length > 0 && (
        <div className="users-table-wrap">
          <table className="users-table">
            <thead><tr><th>Job or ticket</th><th>Part</th><th>Qty</th><th>Added</th></tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.part_id}>
                  <td>
                    {row.task_id && row.task_ref && <Link to={`/tasks/${row.task_id}`}>{row.task_ref}</Link>}
                    {!row.task_id && row.ticket_id && <Link to={`/tickets/${row.ticket_id}`}>{row.ticket_ref || 'Ticket'}</Link>}
                    {!row.task_id && !row.ticket_id && '—'}
                  </td>
                  <td>
                    {row.part_name}
                    {row.part_number && <span className="spare-mono spare-sub"> · {row.part_number}</span>}
                    {row.not_in_list && <span className="spare-badge">Not in the list</span>}
                  </td>
                  <td>{quantityText(row.quantity)}</td>
                  <td>
                    {row.added_by_name ? `${firstNameOf(row.added_by_name)} · ` : ''}
                    {row.fitted_at ? ukDayLabelOf(row.fitted_at) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="section-hint spare-needs-price-hint">
        A team leader, manager or admin can price these on the job or ticket page: open the part, choose Correct and enter its cost.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

function SparePartsSettings() {
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();
  const role = user?.role;
  const manages = canManageSpareParts(role);
  const setsCharge = canSetSparePartsCharge(role);

  // Block 8, for everyone on this tab.
  const [needsPrice, setNeedsPrice] = useState([]);
  const [needsPriceLoading, setNeedsPriceLoading] = useState(true);
  const [needsPriceError, setNeedsPriceError] = useState(null);

  // The list (SPARES_EDIT only).
  const [status, setStatus] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [listLoading, setListLoading] = useState(manages);
  const [listError, setListError] = useState(null);
  const listRequest = useRef(0);

  // Pricing.
  const [pricing, setPricing] = useState(null);
  const [markupInput, setMarkupInput] = useState('');
  const [markupError, setMarkupError] = useState(null);
  const [savingMarkup, setSavingMarkup] = useState(false);

  // Import.
  const fileInput = useRef(null);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null); // { fileName, file, data }
  const [importError, setImportError] = useState(null);
  const [previewAgain, setPreviewAgain] = useState(false);
  const [applying, setApplying] = useState(false);

  // Editing a row.
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', cost: '', charge: '' });
  const [editError, setEditError] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchNeedsPrice = useCallback(async () => {
    setNeedsPriceLoading(true);
    setNeedsPriceError(null);
    try {
      const response = await authFetch('/api/v1/parts/needs-price');
      const data = await answerOf(response);
      if (!response.ok) throw new Error(data.message || 'The parts that need a price could not be loaded.');
      setNeedsPrice(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      setNeedsPriceError(error.message || 'The parts that need a price could not be loaded.');
    } finally {
      setNeedsPriceLoading(false);
    }
  }, [authFetch]);

  const fetchList = useCallback(async () => {
    if (!manages) return;
    const request = listRequest.current + 1;
    listRequest.current = request;
    setListLoading(true);
    setListError(null);
    const params = new URLSearchParams({ status, page: String(page), limit: String(PAGE_SIZE) });
    if (search) params.set('search', search);
    try {
      const response = await authFetch(`/api/v1/parts/catalogue/list?${params.toString()}`);
      const data = await answerOf(response);
      if (request !== listRequest.current) return;
      if (!response.ok) throw new Error(data.message || 'The spare parts list could not be loaded.');
      setList(data);
    } catch (error) {
      if (request === listRequest.current) setListError(error.message || 'The spare parts list could not be loaded.');
    } finally {
      if (request === listRequest.current) setListLoading(false);
    }
  }, [authFetch, manages, status, page, search]);

  const fetchPricing = useCallback(async () => {
    if (!manages) return;
    try {
      const response = await authFetch('/api/v1/parts/catalogue/pricing');
      const data = await answerOf(response);
      if (!response.ok) return;
      setPricing(data);
      setMarkupInput(data.markup_percent ? percentText(data.markup_percent) : '');
    } catch (error) { /* the list's own pricing is shown instead */ }
  }, [authFetch, manages]);

  useEffect(() => { fetchNeedsPrice(); }, [fetchNeedsPrice]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  // The search box waits 300 ms after the last key before it asks again.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const chooseFilter = (key) => {
    setStatus(key);
    setPage(1);
    setEditingId(null);
  };

  // -------------------------------------------------------------------------
  // Pricing
  // -------------------------------------------------------------------------

  const shownPricing = pricing || (list && list.pricing) || null;
  const markupSet = Boolean(shownPricing && shownPricing.markup_set && shownPricing.markup_percent !== null && shownPricing.markup_percent !== undefined);
  const counts = (list && list.counts) || {};
  const noCost = Number(counts.no_cost) || 0;

  const saveMarkup = async (event) => {
    event.preventDefault();
    const text = String(markupInput).trim();
    const valid = /^\d{1,3}(\.\d{1,2})?$/.test(text) && Number(text) <= 500;
    if (!valid) { setMarkupError(MARKUP_MESSAGE); return; }
    setSavingMarkup(true);
    setMarkupError(null);
    try {
      const response = await authFetch('/api/v1/parts/catalogue/pricing', {
        method: 'PUT',
        body: JSON.stringify({ markup_percent: text })
      });
      const data = await answerOf(response);
      if (!response.ok) { setMarkupError(data.message || 'The markup could not be saved.'); return; }
      showToast(`Markup set to ${percentText(data.markup_percent || text)}%. It applies to parts saved from now on.`, 'success');
      await Promise.all([fetchPricing(), fetchList()]);
    } catch (error) {
      setMarkupError('The markup could not be saved.');
    } finally {
      setSavingMarkup(false);
    }
  };

  // -------------------------------------------------------------------------
  // Import and export
  // -------------------------------------------------------------------------

  const sendPreview = async (fileName, file) => {
    setChecking(true);
    setImportError(null);
    setPreviewAgain(false);
    setPreview(null);
    try {
      const response = await authFetch('/api/v1/parts/catalogue/import/preview', {
        method: 'POST',
        body: JSON.stringify({ file_name: fileName, file })
      });
      const data = await answerOf(response);
      if (!response.ok) {
        setImportError(data.message || 'The file could not be checked.');
        return;
      }
      setPreview({ fileName, file, data });
    } catch (error) {
      setImportError('The file could not be checked. Check your connection and try again.');
    } finally {
      setChecking(false);
    }
  };

  const chooseFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    // FileReader, as DocumentsSettings does: the attachments helper refuses
    // .xlsx and .csv.
    const reader = new FileReader();
    reader.onload = () => {
      sendPreview(file.name, reader.result);
      // The same file can be chosen again after a change to it.
      if (fileInput.current) fileInput.current.value = '';
    };
    reader.onerror = () => setImportError('The file could not be read. Choose it again.');
    reader.readAsDataURL(file);
  };

  const applyImport = async () => {
    if (!preview) return;
    const { fileName, file, data } = preview;
    setApplying(true);
    setImportError(null);
    setPreviewAgain(false);
    try {
      const response = await authFetch('/api/v1/parts/catalogue/import/apply', {
        method: 'POST',
        body: JSON.stringify({ file_name: fileName, file, fingerprint: data.fingerprint, plan_hash: data.plan_hash })
      });
      const answer = await answerOf(response);
      if (!response.ok) {
        setImportError(answer.message || 'The import could not be applied.');
        if (response.status === 409) setPreviewAgain(true);
        return;
      }
      const applied = answer.counts || data.counts || {};
      // A row whose name and cost both changed is one changed item, not two.
      const changedKeys = new Set(['name_changes', 'cost_changes', 'brought_back']
        .flatMap(key => listRows(data.lists, key).map(r => String(r.part_number || '').toLowerCase())));
      const changed = changedKeys.size
        || (Number(applied.name_changes) || 0) + (Number(applied.cost_changes) || 0) + (Number(applied.brought_back) || 0);
      showToast(`Spare parts list updated: ${Number(applied.new) || 0} new, ${changed} changed, ${Number(applied.retired) || 0} retired.`, 'success');
      setPreview(null);
      setPage(1);
      await Promise.all([fetchList(), fetchPricing()]);
    } catch (error) {
      setImportError('The import could not be applied. Check your connection and try again.');
    } finally {
      setApplying(false);
    }
  };

  const exportList = async () => {
    const result = await downloadFile(authFetch, '/api/v1/parts/catalogue/export', `spare-parts-${ukTodayKey()}.xlsx`);
    if (!result.ok) showToast(result.message, 'error');
  };

  // -------------------------------------------------------------------------
  // Editing a row
  // -------------------------------------------------------------------------

  const startEdit = (row) => {
    setEditingId(row.catalogue_id);
    setEditError(null);
    setEditForm({
      name: row.part_name || '',
      cost: costInputText(row.purchase_cost_gbp),
      charge: chargeInputText(row.fixed_charge_minor_units)
    });
  };

  const putRow = async (row, body) => {
    const response = await authFetch(`/api/v1/parts/catalogue/${row.catalogue_id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
    const data = await answerOf(response);
    return { ok: response.ok, data };
  };

  const saveEdit = async (row) => {
    const body = {};
    const name = editForm.name.trim();
    if (!name || name.length > 200) { setEditError(NAME_MESSAGE); return; }
    if (name !== row.part_name) body.part_name = name;

    const costTyped = editForm.cost.trim();
    if (costTyped === '') {
      if (row.purchase_cost_gbp !== null && row.purchase_cost_gbp !== undefined) body.purchase_cost_gbp = null;
    } else {
      const cost = costGbpText(costTyped);
      if (cost === null) { setEditError(COST_MESSAGE); return; }
      if (costTenThousandths(cost) !== costTenThousandths(row.purchase_cost_gbp)) body.purchase_cost_gbp = cost;
    }

    // Only a manager or admin may send the own-charge key at all.
    if (setsCharge && editForm.charge.trim() !== chargeInputText(row.fixed_charge_minor_units)) {
      const chargeTyped = editForm.charge.trim();
      const had = row.fixed_charge_minor_units !== null && row.fixed_charge_minor_units !== undefined;
      if (chargeTyped === '') {
        if (had) body.fixed_charge_minor_units = null;
      } else {
        const pence = poundsToPence(chargeTyped);
        if (pence === null) { setEditError(CHARGE_MESSAGE); return; }
        if (!had || pence !== Number(row.fixed_charge_minor_units)) body.fixed_charge_minor_units = pence;
      }
    }

    if (Object.keys(body).length === 0) {
      setEditingId(null);
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      const { ok, data } = await putRow(row, body);
      if (!ok) { setEditError(data.message || 'The change could not be saved.'); return; }
      setEditingId(null);
      showToast('Spare part saved. A new cost or charge applies to parts saved from now on.', 'success');
      await fetchList();
    } catch (error) {
      setEditError('The change could not be saved. Check your connection and try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const setHidden = async (row, hidden) => {
    try {
      const { ok, data } = await putRow(row, { engineer_hidden: hidden });
      if (!ok) { showToast(data.message || 'The change could not be saved.', 'error'); return; }
      showToast(hidden ? 'Hidden from engineers. It stays in the list and in past jobs.' : 'Engineers can find it again.', 'success');
      await fetchList();
    } catch (error) {
      showToast('The change could not be saved. Check your connection and try again.', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const needsPriceBlock = <NeedsPrice rows={needsPrice} loading={needsPriceLoading} error={needsPriceError} />;

  if (!manages) {
    return (
      <div className="spare-parts-settings">
        {toastElement}
        {needsPriceBlock}
      </div>
    );
  }

  const lastImport = list && list.last_import;
  const rows = (list && Array.isArray(list.data)) ? list.data : [];
  const pagination = (list && list.pagination) || { page, totalPages: 1, total: rows.length };
  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const previewData = preview && preview.data;
  const previewCounts = (previewData && previewData.counts) || {};
  const lookalikes = previewData ? listRows(previewData.lists, 'lookalikes') : [];

  return (
    <div className="spare-parts-settings">
      {toastElement}

      <section className="admin-section spare-block">
        <div className="section-header">
          <h2>Spare parts</h2>
        </div>
        <p className="spare-intro">
          The list engineers search in Log Work. Import it from the ServiceM8 materials report (.xlsx or .csv) with the columns Item Number, Name and Purchase Cost.
        </p>
        {lastImport && lastImport.at && (
          <p className="spare-muted">
            Last import: {lastImport.file_name || 'a file'} by {lastImport.by_name || 'someone'}, {ukDayLabelOf(lastImport.at)} at {formatTime(lastImport.at)}.
          </p>
        )}
      </section>

      <section className="admin-section spare-block" aria-labelledby="spare-pricing-title">
        <div className="section-header">
          <h2 id="spare-pricing-title">Customer charge</h2>
        </div>
        <p className="spare-pricing-line">
          {markupSet
            ? `Customer charge: purchase cost plus ${percentText(shownPricing.markup_percent)}%`
            : 'Customer charge: purchase cost (no markup set yet)'}
        </p>
        {!markupSet && shownPricing && (
          <p className="spare-warning" role="status">No markup has been set, so parts are charged at cost. A manager or admin can set it here.</p>
        )}
        {noCost > 0 && (
          <p className="spare-warning" role="status">
            {noCost} {plural(noCost, 'item has', 'items have')} no purchase cost, so {plural(noCost, 'it is', 'they are')} charged £0 until {plural(noCost, 'it is', 'they are')} priced.
            {' '}
            <button type="button" className="spare-link-btn" onClick={() => chooseFilter('no_cost')}>Show them</button>
          </p>
        )}
        {setsCharge ? (
          <form className="spare-markup-form" onSubmit={saveMarkup}>
            <label htmlFor="spare-markup">Markup %</label>
            <input
              id="spare-markup"
              type="number"
              min="0"
              max="500"
              step="0.01"
              inputMode="decimal"
              value={markupInput}
              onChange={event => setMarkupInput(event.target.value)}
            />
            <button type="submit" className="btn-primary" disabled={savingMarkup}>{savingMarkup ? 'Saving…' : 'Save'}</button>
            {markupError && <span className="spare-error" role="alert">{markupError}</span>}
          </form>
        ) : (
          <p className="spare-muted">Set by a manager or admin.</p>
        )}
      </section>

      <section className="admin-section spare-block" aria-labelledby="spare-import-title">
        <div className="section-header">
          <h2 id="spare-import-title">Import and export</h2>
        </div>
        <div className="spare-import-row">
          <label className="spare-file-label" htmlFor="spare-import-file">
            Import a file
            <input
              id="spare-import-file"
              ref={fileInput}
              type="file"
              accept=".xlsx,.csv"
              onChange={chooseFile}
              disabled={checking || applying}
            />
          </label>
          <button type="button" className="spare-plain-btn" onClick={exportList}>Export the list (.xlsx)</button>
        </div>
        {checking && <p className="spare-muted" role="status">Checking the file…</p>}
        {importError && (
          <div className="spare-error-box" role="alert">
            <span>{importError}</span>
            {previewAgain && preview && (
              <button type="button" className="spare-plain-btn" onClick={() => sendPreview(preview.fileName, preview.file)}>Preview again</button>
            )}
          </div>
        )}

        {previewData && (
          <div className="spare-preview" aria-label="Import preview">
            <h3>Preview of {previewData.file_name || preview.fileName}: nothing has changed yet.</h3>
            <ul className="spare-chips">
              {COUNT_CHIPS.map(([key, label]) => (
                <li key={key} className={`spare-chip${Number(previewCounts[key]) > 0 ? ' has-count' : ''}`}>
                  <span className="spare-chip-label">{label}</span>
                  <span className="spare-chip-count">{Number(previewCounts[key]) || 0}</span>
                </li>
              ))}
            </ul>
            {Array.isArray(previewData.notes) && previewData.notes.map(note => (
              <p key={note} className="spare-note">{note}</p>
            ))}
            {PREVIEW_LISTS.map(([key, title, toCells]) => (
              <PreviewList
                key={key}
                title={title}
                rows={listRows(previewData.lists, key)}
                truncated={listTruncated(previewData, key)}
                toCells={toCells}
              />
            ))}
            <LookalikeList groups={lookalikes} />
            <div className="spare-preview-actions">
              <button type="button" className="btn-primary" onClick={applyImport} disabled={applying}>
                {applying ? 'Applying…' : 'Apply these changes'}
              </button>
              <button type="button" className="spare-plain-btn" onClick={() => { setPreview(null); setImportError(null); setPreviewAgain(false); }} disabled={applying}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="admin-section spare-block" aria-labelledby="spare-list-title">
        <div className="section-header">
          <h2 id="spare-list-title">The list</h2>
        </div>
        <div className="spare-filters" role="tablist" aria-label="Which items">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              className={`tab-btn${status === key ? ' active' : ''}`}
              onClick={() => chooseFilter(key)}
            >
              {label} ({Number(counts[key]) || 0})
            </button>
          ))}
        </div>
        <input
          type="search"
          className="spare-search"
          aria-label="Search by name or Item Number"
          placeholder="Search by name or Item Number"
          value={searchInput}
          onChange={event => setSearchInput(event.target.value)}
        />

        {listError && <p className="spare-error" role="alert">{listError}</p>}
        {listLoading && !list && <p className="spare-muted">Loading the spare parts list…</p>}

        {list && (
          rows.length === 0 ? (
            <p className="spare-muted">No items to show.</p>
          ) : (
            <div className="users-table-wrap">
              <table className="users-table spare-table">
                <thead>
                  <tr>
                    <th>Item Number</th>
                    <th>Name</th>
                    <th>Purchase cost</th>
                    <th>Charge per unit</th>
                    <th>Status</th>
                    <th><span className="spare-sr">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const retired = row.is_active === false;
                    const inactive = retired || row.engineer_hidden;
                    if (editingId === row.catalogue_id) {
                      return (
                        <tr key={row.catalogue_id} className="spare-editing">
                          <td className="spare-mono">{row.part_number}</td>
                          <td>
                            <input
                              aria-label="Name"
                              maxLength={200}
                              value={editForm.name}
                              onChange={event => setEditForm(prev => ({ ...prev, name: event.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              aria-label="Purchase cost (£)"
                              inputMode="decimal"
                              placeholder="No cost"
                              value={editForm.cost}
                              onChange={event => setEditForm(prev => ({ ...prev, cost: event.target.value }))}
                            />
                          </td>
                          <td>
                            {setsCharge ? (
                              <input
                                aria-label="Own charge (£)"
                                inputMode="decimal"
                                placeholder="Uses the markup"
                                value={editForm.charge}
                                onChange={event => setEditForm(prev => ({ ...prev, charge: event.target.value }))}
                              />
                            ) : chargeOf(row).text}
                          </td>
                          <td>{statusOf(row)}</td>
                          <td className="user-row-actions">
                            <div className="spare-row-actions">
                              <button type="button" className="btn-primary btn-small" onClick={() => saveEdit(row)} disabled={savingEdit}>
                                {savingEdit ? 'Saving…' : 'Save'}
                              </button>
                              <button type="button" className="spare-plain-btn" onClick={() => setEditingId(null)} disabled={savingEdit}>Cancel</button>
                              {editError && <div className="spare-error" role="alert">{editError}</div>}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const charge = chargeOf(row);
                    return (
                      <tr key={row.catalogue_id} className={inactive ? 'inactive' : ''}>
                        <td className="spare-mono">{row.part_number}</td>
                        <td>{row.part_name}</td>
                        <td>{formatPurchaseCost(row.purchase_cost_gbp)}</td>
                        <td>
                          {charge.text}
                          {charge.own && <span className="spare-badge">Own charge</span>}
                        </td>
                        <td>{statusOf(row)}</td>
                        <td className="user-row-actions">
                          <div className="spare-row-actions">
                            {!retired && (
                              <>
                                <button type="button" className="spare-plain-btn" onClick={() => startEdit(row)} aria-label={`Edit ${row.part_name}`}>Edit</button>
                                <button
                                  type="button"
                                  className="spare-plain-btn"
                                  onClick={() => setHidden(row, !row.engineer_hidden)}
                                  aria-label={`${row.engineer_hidden ? 'Show to engineers' : 'Hide from engineers'}: ${row.part_name}`}
                                >
                                  {row.engineer_hidden ? 'Show to engineers' : 'Hide from engineers'}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {list && totalPages > 1 && (
          <div className="spare-paging">
            <button type="button" className="spare-plain-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || listLoading}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" className="spare-plain-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || listLoading}>Next</button>
          </div>
        )}
      </section>

      {needsPriceBlock}
    </div>
  );
}

export default SparePartsSettings;
